import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getMobileConfig } from '@/features/auth/utils/mobileNumber';
import { useUploadSingleFile } from '@/shared/lib/upload/useUploadSingleFile';
import { useDeleteFile } from '@/shared/lib/upload/useDeleteFile';
import { queryKeys } from '@/shared/lib/query/queryKeys';
import { fetchClientById } from '../../api/clientsApi';
import { useUpdateClientMutation } from '../../api/useClientsQueries';
import {
  AUTHORIZATION_LETTER_MAX_BYTES,
  isPdfFile,
  type AddClientFormValues,
} from '../AddClientPage/AddClientPage.types';

function contactName(user: Record<string, unknown>) {
  const first = String(user.firstName || '').trim();
  const last = String(user.lastName || '').trim();
  return [first, last].filter(Boolean).join(' ');
}

function addressText(user: Record<string, unknown>) {
  if (typeof user.fullAddress === 'string' && user.fullAddress.trim()) {
    return user.fullAddress.trim();
  }
  const address = user.address;
  if (typeof address === 'string') return address;
  if (address && typeof address === 'object') {
    const a = address as Record<string, unknown>;
    return String(a.fullAddress || '').trim();
  }
  return '';
}

function pinText(user: Record<string, unknown>) {
  if (user.pincode) return String(user.pincode);
  if (user.pin) return String(user.pin);
  const address = user.address;
  if (address && typeof address === 'object') {
    return String((address as Record<string, unknown>).pincode || '');
  }
  return '';
}

function existingLetterName(user: Record<string, unknown>) {
  const docs = user.documents;
  if (!docs || typeof docs !== 'object') return null;
  const entity = (docs as Record<string, unknown>).entity;
  if (!entity) return null;
  if (typeof entity === 'object') {
    const file = entity as { filename?: string; url?: string };
    return file.filename || (file.url ? 'Authorization letter.pdf' : null);
  }
  return 'Authorization letter.pdf';
}

/** Edit-client form: loads client, validates with env mobile rules, PATCHes /v1/app/clients/:id. */
export function useEditClientForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const mobileConfig = useMemo(() => getMobileConfig(), []);
  const updateMut = useUpdateClientMutation();
  const uploadMut = useUploadSingleFile();
  const deleteFile = useDeleteFile();

  const [authorizationLetterMediaId, setAuthorizationLetterMediaId] = useState<string | null>(
    null,
  );
  const [authorizationLetterFileName, setAuthorizationLetterFileName] = useState<string | null>(
    null,
  );
  const [authorizationLetterError, setAuthorizationLetterError] = useState<string | undefined>();
  const [hydrated, setHydrated] = useState(false);

  const detailQ = useQuery({
    queryKey: queryKeys.clients.detail(id || ''),
    queryFn: () => fetchClientById(id!),
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<AddClientFormValues>({ mode: 'onTouched' });

  useEffect(() => {
    const raw = detailQ.data?.data?.client || detailQ.data?.data?.user || detailQ.data?.data;
    if (!raw || typeof raw !== 'object' || hydrated) return;
    const user = raw as Record<string, unknown>;
    const dynamicFields =
      user.dynamicFields && typeof user.dynamicFields === 'object'
        ? (user.dynamicFields as Record<string, unknown>)
        : {};
    reset({
      clientName: String(user.companyName || user.entityName || ''),
      businessCategory: String(user.businessCategory || user.industry || ''),
      businessSubCategory: String(
        user.businessSubCategory || dynamicFields.businessSubCategory || '',
      ),
      contactPersonName: contactName(user),
      mobileNumber: String(user.mobileNumber || '').replace(/\D/g, ''),
      email: String(user.email || ''),
      address: addressText(user),
      pin: pinText(user),
    });
    setAuthorizationLetterFileName(existingLetterName(user));
    setHydrated(true);
  }, [detailQ.data, hydrated, reset]);

  const clearAuthorizationLetter = () => {
    const id = authorizationLetterMediaId?.trim();
    if (id) {
      void deleteFile.mutateAsync({ mediaId: id }).catch(() => {});
    }
    setAuthorizationLetterMediaId(null);
    setAuthorizationLetterFileName(null);
    setAuthorizationLetterError(undefined);
  };

  const onSelectAuthorizationLetter = async (file: File) => {
    setAuthorizationLetterError(undefined);
    if (!isPdfFile(file)) {
      setAuthorizationLetterError('Only PDF files are allowed');
      toast.error('Only PDF files are allowed');
      return;
    }
    if (file.size > AUTHORIZATION_LETTER_MAX_BYTES) {
      setAuthorizationLetterError('PDF must be under 10MB');
      toast.error('PDF must be under 10MB');
      return;
    }

    const previousId = authorizationLetterMediaId?.trim();
    if (previousId) {
      void deleteFile.mutateAsync({ mediaId: previousId }).catch(() => {});
    }

    setAuthorizationLetterFileName(file.name);
    setAuthorizationLetterMediaId(null);

    try {
      const response = await uploadMut.mutateAsync({
        file,
        moduleName: 'authorization-letter',
      });
      const mediaId = response?.data?.mediaId as string | undefined;
      if (!mediaId) throw new Error('Upload did not return a media id');
      setAuthorizationLetterMediaId(mediaId);
    } catch (err) {
      clearAuthorizationLetter();
      const message =
        err instanceof Error ? err.message : 'Failed to upload authorization letter';
      setAuthorizationLetterError(message);
      toast.error(message);
    }
  };

  const onSubmit = (data: AddClientFormValues) => {
    if (!id) return;
    if (!mobileConfig.regex.test(data.mobileNumber.trim())) {
      setError('mobileNumber', { type: 'pattern', message: mobileConfig.message });
      toast.error(mobileConfig.message);
      return;
    }

    const nameParts = data.contactPersonName.trim().split(/\s+/);
    const firstName = nameParts[0] || data.clientName.trim();
    const lastName = nameParts.slice(1).join(' ') || '';

    const payload: Record<string, unknown> = {
      companyName: data.clientName.trim(),
      firstName,
      lastName,
      mobileNumber: data.mobileNumber.trim(),
      mobileCountryCode: mobileConfig.code,
      businessCategory: data.businessCategory,
      businessSubCategory: data.businessSubCategory,
      address: data.address.trim(),
      pincode: data.pin.trim(),
      pin: data.pin.trim(),
      fullAddress: data.address.trim(),
    };

    if (authorizationLetterMediaId) {
      payload.documents = { entity: authorizationLetterMediaId };
    }

    updateMut.mutate(
      { clientId: id, payload },
      { onSuccess: () => navigate(`/clients/${id}`) },
    );
  };

  return {
    id,
    navigate,
    mobileConfig,
    register,
    control,
    setValue,
    watch,
    setError,
    clearErrors,
    errors,
    handleSubmit: handleSubmit(onSubmit),
    isLoading: detailQ.isPending || !hydrated,
    isSaving: updateMut.isPending,
    isNotFound: detailQ.isError || (!detailQ.isPending && !detailQ.data),
    authorizationLetterFileName,
    authorizationLetterError,
    isUploadingAuthorizationLetter: uploadMut.isPending,
    onSelectAuthorizationLetter,
    clearAuthorizationLetter,
  };
}
